import { cn } from "../lib/utils.js";

/**
 * Toggle-chips for a project's rule groups, sourced strictly from the groups
 * the synced rules repo actually has — a typo'd group is a silent no-op (the
 * project matches zero rules and nothing tells you), which is exactly the
 * quiet-failure class this product refuses everywhere else. Free text isn't
 * offered on purpose.
 *
 * A group the project still carries but the sync no longer has (a renamed or
 * deleted directory) stays visible and selected — the assignment doesn't
 * silently vanish, but its chip is dashed to read as stale.
 */
export function GroupsEditor({
  groups,
  knownGroups,
  onChange,
  className,
}: {
  groups: readonly string[];
  knownGroups: readonly string[];
  onChange: (groups: string[]) => void;
  className?: string;
}) {
  const all = [...new Set([...knownGroups, ...groups])].sort();

  function toggle(group: string) {
    onChange(groups.includes(group) ? groups.filter((g) => g !== group) : [...groups, group]);
  }

  if (all.length === 0) {
    return (
      <p className={cn("text-xs text-muted-foreground", className)}>
        Noch keine Gruppen im Regelwerk — die Regeln liegen wahrscheinlich noch flach unter{" "}
        <code className="font-mono">rules/</code>.
      </p>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {all.map((group) => {
        const selected = groups.includes(group);
        const stale = selected && !knownGroups.includes(group);
        return (
          <button
            key={group}
            type="button"
            onClick={() => toggle(group)}
            aria-pressed={selected}
            title={stale ? `„${group}“ hat aktuell keine Regeln mehr im Sync` : undefined}
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors",
              selected
                ? "bg-foreground text-background hover:bg-foreground/90"
                : "border border-border bg-card text-foreground hover:bg-muted",
              stale && "border border-dashed border-current",
            )}
          >
            {group}
          </button>
        );
      })}
    </div>
  );
}
